import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';

import { paths } from 'routes';

import { useCompetenceAreas } from 'api/getCompetenceAreas';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { MultiSelectDialog } from 'components/Form/MultiSelectDialog';
import { IconWrapper } from 'components/Icon';
import { ContentLayout } from 'components/Layout';
import { Loader } from 'components/Loader';
import { NotFound } from 'components/NotFound/NotFound';
import { Pagination } from 'components/Pagination';
import { Search } from 'components/Search';
import { usePageSize } from 'hook/usePageSize';

import { useSearchSuppliers } from '../api/searchSuppliers';
import { getSupplierSearchPreferences, saveSupplierSearchPreferences } from '../api/supplierSearchPreferences';
import { SupplierListItem } from '../components/SupplierListItem';

const defaultFilters = {
  competenceAreas: [],
  starred: false,
};

const filterButtonSx = {
  backgroundColor: 'background.paper',
  borderRadius: 1,
  fontWeight: 400,
  lineHeight: '24px',
  fontFeatureSettings: '"liga" off, "cling" off',
  boxShadow: '0 2px 4px 0 rgba(19, 55, 63, 0.20)',
  color: 'text.primary',
  borderColor: 'border.jobAd',
};

const filterIconSx = {
  height: 14,
  width: 14,
  svg: {
    stroke: '#000',
    strokeWidth: 1,
    height: 14,
    width: 14,
    mt: -1.25,
  },
};

export const SupplierSearch = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [filters, setFilters] = useState(defaultFilters);
  const [isLoaded, setIsLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [isAreasDialogOpen, setIsAreasDialogOpen] = useState(false);
  const [selectedCompetenceAreas, setSelectedCompetenceAreas] = useState([]);

  const { data: competences, isLoading: isAreasLoading } = useCompetenceAreas();

  const allAreas = useMemo(() => (
    (competences?.competences ?? []).map((item) => ({ id: item.id, label: item.name }))
  ), [competences]);

  useEffect(() => {
    let active = true;

    getSupplierSearchPreferences()
      .then((saved) => {
        if (active && saved) {
          setFilters({
            competenceAreas: Array.isArray(saved.competenceAreas) ? saved.competenceAreas : [],
            starred: !!saved.starred,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) setIsLoaded(true);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setSelectedCompetenceAreas(allAreas.filter((option) => filters.competenceAreas.includes(option.id)));
  }, [filters.competenceAreas, allAreas]);

  const updateFilters = (changes) => {
    const next = { ...filters, ...changes };
    setFilters(next);
    setPage(1);
    saveSupplierSearchPreferences(next).catch(() => {
      enqueueSnackbar('Failed to save supplier search filters', { variant: 'warning' });
    });
  };

  const hasCriteria = search.trim().length > 0 || filters.competenceAreas.length > 0 || filters.starred;

  const {
    data,
    isPending,
    isError,
    refetch,
  } = useSearchSuppliers({
    search: search.trim(),
    competenceAreas: filters.competenceAreas,
    starred: filters.starred,
    page: page - 1,
    limit: pageSize,
  }, { enabled: isLoaded && hasCriteria });

  const suppliers = data?.suppliers ?? [];
  const total = data?.total ?? 0;

  const debouncedSearchChange = useDebouncedCallback((value) => {
    setSearch(value);
    setPage(1);
  }, 300);
  const handleSearchChange = ({ target: { value } }) => debouncedSearchChange(value);

  const handleCompetenceAreasChange = (event) => {
    const { value } = event.target;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    setSelectedCompetenceAreas(allAreas.filter((option) => selectedIds.includes(option.id)));
  };

  const handleCompetenceAreasConfirmSelection = (value) => {
    updateFilters({ competenceAreas: value.map((item) => item.id) });
  };

  const handleCompetenceAreasDelete = (deletedId) => (event) => {
    event.stopPropagation();
    setSelectedCompetenceAreas((selected) => selected.filter((item) => item.id !== deletedId));
  };

  const renderResults = () => {
    if (!isLoaded) return <Loader />;

    if (!hasCriteria) {
      return (
        <NotFound
          wrapperSx={{ mt: 19 }}
          title="Find suppliers"
          caption="Enter a search term or select competence areas to find suppliers."
        />
      );
    }

    if (isPending) return <Loader />;

    if (isError) {
      return (
        <Stack height="100%" alignItems="center" justifyContent="center">
          <Typography variant="h5">Failed to load suppliers</Typography>
        </Stack>
      );
    }

    return (
      <>
        <Stack
          flex={1}
          mt={2}
          py={2}
          bgcolor="background.paper"
          borderTop="1px solid"
          borderColor="border.main"
          gap={3.5}
        >
          {suppliers.length === 0 && (
            <NotFound wrapperSx={{ mt: 19 }} title="No suppliers found" />
          )}
          {suppliers.map((supplier) => (
            <SupplierListItem
              key={supplier.company_id}
              {...supplier}
              onStarChange={filters.starred ? () => refetch() : undefined}
            />
          ))}
        </Stack>
        <Stack direction="row" justifyContent="center">
          <Pagination
            page={page}
            handlePageChange={(_, value) => setPage(value)}
            itemCount={total}
            itemsPerPage={pageSize}
            onItemsPerPageChange={setPageSize}
          />
        </Stack>
      </>
    );
  };

  return (
    <ContentLayout>
      <Stack height={1}>
        <BreadCrumbs
          items={[
            { name: '', to: paths.supplierSearch },
            { name: `Find Suppliers${hasCriteria && total > 0 ? ` (${total})` : ''}` },
          ]}
        />

        <Stack
          px={4}
          py={2}
          bgcolor="background.paper"
          borderTop="1px solid"
          borderColor="border.main"
          height="100%"
        >
          <Stack direction="column" gap={2.5} alignItems="center">
            <Search
              placeholder="Search suppliers"
              handleSearchChange={handleSearchChange}
              sx={{
                minWidth: 200,
                height: 40,
                py: 2.5,
                px: 3.5,
                borderRadius: 1,
                alignSelf: 'stretch',
                border: '1px solid',
                borderColor: '#13373F33',
                backgroundColor: 'background.paper',
                boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.08)',
              }}
              inputSx={{
                border: 'none',
                height: 1,
                '& .MuiOutlinedInput-input': {
                  fontSize: '1rem',
                  fontWeight: 400,
                  color: 'text.black',
                  lineHeight: '24px',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.placeholder',
                  fontSize: '1rem',
                  fontWeight: 400,
                  lineHeight: '24px',
                },
                pl: 0,
                pr: 0,
              }}
              iconSx={{
                svg: {
                  height: 24,
                  color: 'text.black',
                  stroke: '#000',
                  strokeWidth: 0,
                },
              }}
            />
            <Stack direction="row" gap={2} alignItems="flex-start" width={1} sx={{ flexWrap: 'wrap' }}>
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => setIsAreasDialogOpen(true)}
                  disabled={isAreasLoading}
                  sx={filterButtonSx}
                  endIcon={<IconWrapper icon="chevronDown" sx={{ ...filterIconSx, ml: 1 }} height={14} />}
                >
                  Competence Areas {selectedCompetenceAreas.length > 0 && `(${selectedCompetenceAreas.length}/${allAreas.length > 0 ? allAreas.length : '...'})`}
                </Button>
                <MultiSelectDialog
                  name="Competence Areas"
                  open={isAreasDialogOpen}
                  onClose={() => setIsAreasDialogOpen(false)}
                  onConfirm={handleCompetenceAreasConfirmSelection}
                  onChange={handleCompetenceAreasChange}
                  onDelete={handleCompetenceAreasDelete}
                  value={selectedCompetenceAreas}
                  treeData={allAreas}
                />
              </Box>
              <Button
                variant="outlined"
                onClick={() => updateFilters({ starred: !filters.starred })}
                aria-pressed={filters.starred}
                sx={{ ...filterButtonSx, borderColor: filters.starred ? 'warning.main' : 'border.jobAd' }}
                startIcon={
                  <IconWrapper
                    icon={filters.starred ? 'star' : 'starOutline'}
                    sx={{
                      height: 16,
                      width: 16,
                      svg: {
                        color: filters.starred ? theme.palette.warning.main : theme.palette.grey[500],
                        stroke: filters.starred ? theme.palette.warning.main : theme.palette.grey[500],
                        strokeWidth: filters.starred ? 0 : 1.5,
                        height: 16,
                        width: 16,
                        mt: -1,
                      },
                    }}
                    height={16}
                  />
                }
              >
                Starred
              </Button>
              <Button
                variant="outlined"
                onClick={() => updateFilters(defaultFilters)}
                startIcon={<IconWrapper icon="xMark" sx={{ ...filterIconSx, mr: 1 }} height={14} />}
                sx={{ ...filterButtonSx, backgroundColor: 'background.light' }}
              >
                Clear All
              </Button>
            </Stack>
          </Stack>

          {renderResults()}
        </Stack>
      </Stack>
    </ContentLayout>
  );
};
